import React, { useEffect, useMemo, useState, useCallback } from "react";
import { apiGet, apiPut, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Paintbrush, Loader2, CheckCircle2, Image as ImageIcon, Plus, Trash2, Save, X, Pencil } from "lucide-react";

const fmt = (v, dec = 0) => (v === null || v === undefined || v === "" ? "-" : Number(v).toFixed(dec));

const DIRECTII = [
  { cod: "R", nume: "Dreapta" },
  { cod: "L", nume: "Stanga" },
  { cod: "U", nume: "Sus" },
  { cod: "D", nume: "Jos" },
];

export default function PerimetruObloanePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [segments, setSegments] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("/finisaj/perimetru-obloane");
      setItems(res.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const editing = useMemo(() => items.find((r) => r.id === editId) || null, [items, editId]);

  const openEditor = (r) => {
    setEditId(r.id);
    setSegments((r.traseu || []).map((s) => ({ directie: s.directie, lungime_mm: String(s.lungime_mm) })));
  };

  const closeEditor = () => {
    setEditId(null);
    setSegments([]);
  };

  const addSegment = () => setSegments((prev) => [...prev, { directie: "R", lungime_mm: "" }]);
  const removeSegment = (i) => setSegments((prev) => prev.filter((_, idx) => idx !== i));
  const setSeg = (i, key, val) =>
    setSegments((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));

  const preview = useMemo(() => {
    let dx = 0, dy = 0, per = 0, ok = true;
    for (const s of segments) {
      const l = parseFloat(s.lungime_mm);
      if (Number.isNaN(l) || l <= 0) { ok = false; continue; }
      per += l;
      if (s.directie === "R") dx += l;
      else if (s.directie === "L") dx -= l;
      else if (s.directie === "U") dy += l;
      else if (s.directie === "D") dy -= l;
    }
    return { per, dx, dy, inchis: ok && dx === 0 && dy === 0 && segments.length > 0, valid: ok };
  }, [segments]);

  const save = async () => {
    const payload = { traseu: [] };
    for (const s of segments) {
      const l = parseFloat(s.lungime_mm);
      if (Number.isNaN(l) || l <= 0) {
        toast.error("Fiecare segment trebuie sa aiba o lungime reala > 0 (mm).");
        return;
      }
      payload.traseu.push({ directie: s.directie, lungime_mm: l });
    }
    setSaving(true);
    try {
      const fresh = await apiPut(`/finisaj/perimetru-obloane/${encodeURIComponent(editId)}`, payload);
      setItems((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
      if (payload.traseu.length === 0) toast.success(`Cote sterse pentru ${fresh.profil_nume}`);
      else if (fresh.validat) toast.success(`Calculat: ${fresh.profil_nume} = ${fresh.perimetru_desfasurat_mm} mm (contur inchis)`);
      else toast.warning(`Salvat, dar conturul nu este inchis pentru ${fresh.profil_nume}`);
      closeEditor();
    } catch (ex) {
      toast.error(ex.message);
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right focus:outline-none focus:border-amber-500";

  return (
    <div className="space-y-6" data-testid="perimetru-obloane-page">
      <div className="flex items-center gap-3">
        <Paintbrush className="h-6 w-6 text-amber-600" />
        <h1 className="text-2xl font-bold text-slate-900">Vopsire obloane - perimetru profile</h1>
      </div>
      <p className="text-sm text-slate-500 -mt-3" data-testid="perimetru-obloane-info">
        Cele 4 profile prelucrate de oblon: toc, cercevea, traversa, lamela. Perimetrul desfasurat de
        vopsire (mp/ml = perimetru / 1000) se calculeaza determinist din cotele conturului sectiunii
        (traseu R/L/U/D). Valoarea provizorie este gabaritul 2*(latime+inaltime). Statusul devine validat
        doar cand traseul este definit si conturul este inchis. Unde lipseste o cota reala, valoarea este "-".
      </p>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se incarca...
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="perimetru-obloane-empty">
            Niciun profil de oblon importat.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[1040px]" data-testid="perimetru-obloane-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-600">
                <th className="px-4 py-3 font-medium">Foto</th>
                <th className="px-4 py-3 font-medium">Profil</th>
                <th className="px-4 py-3 font-medium">Tip</th>
                <th className="px-4 py-3 font-medium text-center">Sectiune</th>
                <th className="px-4 py-3 font-medium text-center">Brut</th>
                <th className="px-4 py-3 font-medium text-center">Interior</th>
                <th className="px-4 py-3 font-medium text-center">Gabarit min (mm)</th>
                <th className="px-4 py-3 font-medium text-center">Perimetru desfasurat</th>
                <th className="px-4 py-3 font-medium text-center">mp/ml</th>
                <th className="px-4 py-3 font-medium text-center">Contur</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`perimetru-obloane-row-${r.cod}`}>
                  <td className="px-4 py-3">
                    {r.imagine_url ? (
                      <img
                        src={`${BACKEND_URL}${r.imagine_url}`}
                        alt={r.cod || "profil"}
                        data-testid={`perimetru-obloane-img-${r.cod}`}
                        className="w-12 h-12 object-contain rounded border border-slate-200 bg-white"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                      />
                    ) : null}
                    <div className="w-12 h-12 bg-slate-100 rounded items-center justify-center" style={{ display: r.imagine_url ? "none" : "flex" }}>
                      <ImageIcon className="h-4 w-4 text-slate-300" />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.profil_nume}</div>
                    <div className="font-mono text-xs text-slate-400">{r.cod}</div>
                  </td>
                  <td className="px-4 py-3"><span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{r.tip}</span></td>
                  <td className="px-4 py-3 text-center text-slate-600">{r.sectiune_latime || "-"}x{r.sectiune_inaltime || "-"}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{r.profil_brut_sectiune}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{r.interior}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{fmt(r.gabarit_minim_mm)}</td>
                  <td className="px-4 py-3 text-center font-semibold text-amber-700" data-testid={`perimetru-obloane-desf-${r.cod}`}>
                    {r.perimetru_desfasurat_mm === null || r.perimetru_desfasurat_mm === undefined ? "-" : `${fmt(r.perimetru_desfasurat_mm)} mm`}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-700">{r.mp_ml === null ? "-" : r.mp_ml}</td>
                  <td className="px-4 py-3 text-center">
                    {r.contur_inchis === null || r.contur_inchis === undefined ? (
                      <span className="text-slate-400 text-xs">-</span>
                    ) : r.contur_inchis ? (
                      <span className="text-green-700 text-xs font-medium">inchis</span>
                    ) : (
                      <span className="text-red-600 text-xs font-medium">deschis</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {r.validat ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" data-testid={`perimetru-obloane-status-${r.cod}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Validat
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" data-testid={`perimetru-obloane-status-${r.cod}`}>
                        Provizoriu
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEditor(r)}
                      data-testid={`perimetru-obloane-edit-${r.cod}`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-medium transition-colors"
                      title="Defineste cotele conturului sectiunii"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editeaza cote
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="perimetru-obloane-editor">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">Cote contur sectiune - {editing.profil_nume}</div>
              <div className="font-mono text-xs text-slate-400">{editing.cod} | gabarit {fmt(editing.gabarit_minim_mm)} mm</div>
            </div>
            <button onClick={closeEditor} data-testid="perimetru-obloane-editor-close" className="text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-xs text-slate-500">
            Introduceti conturul ortogonal al sectiunii, segment cu segment, in sens de parcurgere.
            Directie: R dreapta, L stanga, U sus, D jos. Perimetrul = suma lungimilor. Conturul trebuie
            sa fie inchis (revine in punctul de start) pentru validare.
          </p>

          <div className="space-y-2">
            {segments.map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`perimetru-obloane-seg-${i}`}>
                <span className="w-6 text-xs text-slate-400 text-right">{i + 1}.</span>
                <select
                  value={s.directie}
                  onChange={(e) => setSeg(i, "directie", e.target.value)}
                  data-testid={`perimetru-obloane-seg-dir-${i}`}
                  className="h-9 px-2 rounded-md border border-slate-300 bg-white text-sm"
                >
                  {DIRECTII.map((d) => (<option key={d.cod} value={d.cod}>{d.cod} - {d.nume}</option>))}
                </select>
                <input
                  type="number" min="1" step="0.1"
                  value={s.lungime_mm}
                  onChange={(e) => setSeg(i, "lungime_mm", e.target.value)}
                  data-testid={`perimetru-obloane-seg-len-${i}`}
                  className={inp} placeholder="mm"
                />
                <span className="text-xs text-slate-400">mm</span>
                <button onClick={() => removeSegment(i)} data-testid={`perimetru-obloane-seg-del-${i}`} className="text-slate-400 hover:text-red-600 p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {segments.length === 0 && (
              <div className="text-sm text-slate-400">Niciun segment. Adaugati cote sau salvati gol pentru a reveni la gabarit provizoriu.</div>
            )}
          </div>

          <button onClick={addSegment} data-testid="perimetru-obloane-seg-add" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 px-3 py-1.5 text-xs font-medium transition-colors">
            <Plus className="h-3.5 w-3.5" /> Adauga segment
          </button>

          <div className="flex items-center gap-4 text-sm border-t border-slate-100 pt-3" data-testid="perimetru-obloane-preview">
            <span className="text-slate-600">Perimetru: <b className="text-amber-700">{preview.valid ? fmt(preview.per) : "-"} mm</b></span>
            <span className="text-slate-600">Eroare inchidere: dx={preview.dx}, dy={preview.dy}</span>
            {segments.length > 0 && (
              preview.inchis
                ? <span className="text-green-700 font-medium">contur inchis</span>
                : <span className="text-red-600 font-medium">contur deschis</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} data-testid="perimetru-obloane-editor-save" className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salveaza cotele
            </button>
            <button onClick={closeEditor} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 px-4 py-2 text-sm font-medium transition-colors">
              Renunta
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
